import React, { useEffect, useState } from 'react';
import { FieldGroup, Modal, PillButton, Row, Stack, Text, TimePicker } from 'library/ui';
import { useMediaQuery } from '@mui/material';
import theme from 'theme';
import useLoading from 'library/hooks/useLoading';
import { client, consoleLogInDev } from 'shared';
import { buildTimeInZone, toTimeInput } from 'shared/shiftTime';
import moment from 'moment-timezone';
import WatchLaterIcon from '@mui/icons-material/WatchLater';

interface ClockInButtonProps {
    onComplete: () => void;
    appointmentId: number;
    startDate: string;
    providerName: string;
    providerUserId: number;
    timezone: string;
}

export default function ClockInButton({
    onComplete,
    appointmentId,
    startDate,
    providerName,
    providerUserId,
    timezone,
}: ClockInButtonProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <PillButton onClick={() => setIsModalOpen(true)} icon={<WatchLaterIcon style={{ fontSize: 18 }} />}>
                Clock in
            </PillButton>
            <ClockInModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onComplete={() => {
                    setIsModalOpen(false);
                    onComplete();
                }}
                appointmentId={appointmentId}
                startDate={startDate}
                providerName={providerName}
                providerUserId={providerUserId}
                timezone={timezone}
            />
        </>
    );
}

export function ClockInModal({
    open,
    onClose,
    onComplete,
    appointmentId,
    startDate,
    providerName,
    providerUserId,
    timezone,
}: {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
    appointmentId: number;
    startDate: string;
    providerName: string;
    providerUserId: number;
    timezone: string;
}) {
    const [clockInTime, setClockInTime] = useState<moment.Moment | null | undefined>(moment.tz(startDate, timezone));
    const [isClockInValid, setIsClockInValid] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        setClockInTime(moment.tz(startDate, timezone));
    }, [startDate]);

    const small = useMediaQuery(theme.breakpoints.down('md'));
    const { loading, setLoading } = useLoading();
    function submit() {
        setLoading(true);
        const clockInIsoString = clockInTime?.toISOString();

        client(`api/hour-report/clock-in/`, {
            method: 'POST',
            body: {
                appointment_id: appointmentId.toString(),
                provider_user_id: providerUserId?.toString(),
                provider_clock_in: clockInIsoString,
                business_clocked_provider_in: clockInIsoString,
            },
        })
            .then(onComplete)
            .catch((error) => {
                consoleLogInDev(error);
                setErrorMessage(error.message || 'There was an error clocking the provider in');
            })
            .finally(() => {
                setLoading(false);
                onClose();
            });
    }

    function onClockInChange(value: moment.Moment | null) {
        setIsClockInValid(value?.isValid() || false);
        setClockInTime(moment(value));
    }

    if (!open) return null;

    return (
        <Modal title={`Clock ${providerName} in`} onClose={onClose} width={small ? 400 : 500}>
            <Stack gap="md">
                <Text weight="bold">Ensure {providerName} is clocked in by entering their arrival time below.</Text>
                <FieldGroup label="Clock in time">
                    <TimePicker
                        value={toTimeInput(clockInTime, timezone)}
                        onChange={(value) => onClockInChange(buildTimeInZone(value, startDate, timezone))}
                    />
                </FieldGroup>
                <Row justify="end">
                    <PillButton disabled={loading || !clockInTime || !isClockInValid} onClick={submit}>
                        {loading ? 'Clocking in...' : 'Clock In'}
                    </PillButton>
                </Row>
                {errorMessage && <Text tone="error">{errorMessage}</Text>}
            </Stack>
        </Modal>
    );
}
